import React, { useCallback, useEffect } from "react";
import { useFormik } from "formik";

import {
  Button,
  CustomSelect,
  Error,
  Input,
  Label,
  TimePickerComponent,
} from "../../Components";
import { exchangeCreateSchema } from "../../Schema/ExchangeSchema";
import { convertToAMPM, convertTo24Hour } from "../../helper/common";
import {
  API_EXCHANGE_CREATE,
  API_EXCHANGE_UPDATE,
} from "../../utils/api/ApiConstant";
import { patchAxios, postAxios } from "../../Services/commonService";
import { viaOptions } from "../../Static/data";

const ExchangeCreate = ({ rowData, handleShowHide }) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    initialValues: {
      exchangeName: rowData?.exchangeName || "",
      displayOrder: rowData?.displayOrder || 0,
      via: rowData?.via || "",
      isActive: rowData?.isActive || false,
      isAutomatic: rowData?.isAutomatic || false,
      startDate: convertTo24Hour(rowData?.startDate) || null,
      endDate: convertTo24Hour(rowData?.endDate) || null,
    },
    validationSchema: exchangeCreateSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const payload = {
        ...values,
        via: values.via.value,
        startDate: convertToAMPM(values.startDate),
        endDate: convertToAMPM(values.endDate),
      };

      handleFormSubmit({ payload });
    },
  });

  const handleFormSubmit = useCallback(async ({ payload }) => {
    let res;
    if (rowData && rowData?._id) {
      res = await patchAxios({
        url: `${API_EXCHANGE_UPDATE}/${rowData._id}`,
        payload,
      });
    } else {
      res = await postAxios({ url: API_EXCHANGE_CREATE, payload });
    }

    if (res) {
      handleShowHide();
    }
  }, []);

  useEffect(() => {
    if (rowData?.via) {
      const selectedVia = viaOptions.find((x) => x.value === rowData?.via);

      if (selectedVia) setFieldValue("via", selectedVia);
    }

    if (rowData?.startDate) {
      const formattedStartDate = convertTo24Hour(rowData?.startDate);
      setFieldValue("startDate", formattedStartDate);
    }

    if (rowData?.endDate) {
      const formattedEndDate = convertTo24Hour(rowData?.endDate);
      setFieldValue("endDate", formattedEndDate);
    }
  }, [rowData?.via, rowData?.startDate, rowData?.endDate]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="row m-0 p-0">
        <div className="col-md-6 m-0 p-1">
          <Label
            htmlFor="exchangeName"
            className="form-label"
            isRequired={true}
          >
            Exchange name
          </Label>
          <Input
            type="text"
            className="form-control"
            name="exchangeName"
            id="exchangeName"
            placeholder="Enter exchange Name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.exchangeName}
            autoFocus={true}
            disabled={rowData && rowData?._id}
          />
          {errors.exchangeName && touched.exchangeName && (
            <Error>{errors.exchangeName}</Error>
          )}
        </div>

        {!rowData?._id && (
          <div className="col-md-6 m-0 p-1">
            <Label
              htmlFor="displayOrder"
              className="form-label"
              isRequired={true}
            >
              Display order
            </Label>
            <Input
              type="number"
              className="form-control"
              name="displayOrder"
              id="displayOrder"
              placeholder="Enter displayOrder"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.displayOrder}
            />
            {errors.displayOrder && touched.displayOrder && (
              <Error>{errors.displayOrder}</Error>
            )}
          </div>
        )}

        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="via" className="form-label" isRequired={true}>
            Via
          </Label>
          <CustomSelect
            options={viaOptions}
            value={values.via}
            onChange={(selectedOption) => {
              setValues({ ...values, via: selectedOption });
            }}
            placeholder="Select via"
            isMulti={false}
            isDisabled={rowData && rowData?._id}
          />
          {errors.via && touched.via && <Error>{errors.via}</Error>}
        </div>

        {rowData && rowData?._id && (
          <>
            <div className="col-md-6 m-0 p-1">
              <Label htmlFor="startDate" className="form-label">
                Start Time
              </Label>
              <TimePickerComponent
                selectedTime={values.startDate}
                onChange={(time) => {
                  setFieldValue("startDate", time);
                  setFieldTouched("startDate", true); // Mark as touched for validation
                }}
              />
              {errors.startDate && touched.startDate && (
                <Error>{errors.startDate}</Error>
              )}
            </div>

            <div className="col-md-6 m-0 p-1">
              <Label htmlFor="endDate" className="form-label">
                End Time
              </Label>
              <TimePickerComponent
                selectedTime={values.endDate}
                onChange={(time) => {
                  setFieldValue("endDate", time);
                  setFieldTouched("endDate", true); // Mark as touched for validation
                }}
              />
              {errors.endDate && touched.endDate && (
                <Error>{errors.endDate}</Error>
              )}
            </div>
          </>
        )}
      </div>
      <Button type="submit" id="form-submit-btn" hidden={true}>
        Submit
      </Button>
    </form>
  );
};

export default ExchangeCreate;
